import React, { useEffect, useState } from "react";

import { useForm } from "../useForm";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import {
  classNames,
  generateReceivingLotNumber,
  standardWeightOptions,
  stockUnitOfMeasureOptions,
} from "../../../shared/utility";
import ButtonSpinner from "../../controls/ButtonSpinner";
import DollarInputText from "../../controls/inputs/DollarInputText";
import { ProgressSpinner } from "primereact/progressspinner";

let lastAddItemValues = null;

const initialFValues = {
  net_weight: 0,
  tare_weight: 0,
  weight_type: { value: -1 },
  carton_count: 0,
  unit_cost: 0,
  piece_count: 0,
  total_cost: 0,
  is_pallet: false,
};

export default function AddItemToReceivingForm(props) {
  const {
    addItemToReceiving,
    isEdit,
    product,
    supplierNumber,
    getWarehouses,
    getWarehouseLocations,
  } = props;

  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseLocationsOptions, setWarehouseLocationsOptions] = useState(
    []
  );
  const [loadingData, setLoadingData] = useState(true);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("lot_number" in fieldValues) {
      temp.lot_number = null;
      if (fieldValues.lot_number === "") {
        temp.lot_number = "This field is required.";
      }
    }

    if ("net_weight" in fieldValues) {
      temp.net_weight = null;
      if (fieldValues.net_weight === "" || fieldValues.net_weight === 0) {
        temp.net_weight = "This field is required.";
      }
    }

    if ("tare_weight" in fieldValues) {
      temp.tare_weight = null;
      if (fieldValues.tare_weight === "") {
        temp.tare_weight = "This field is required.";
      }
    }

    if ("weight_type" in fieldValues) {
      temp.weight_type = null;
      if (fieldValues.weight_type) {
        if (fieldValues.weight_type.value === -1) {
          temp.weight_type = "This field is required.";
        }
      } else {
        temp.weight_type = "This field is required.";
      }
    }

    if ("carton_count" in fieldValues) {
      temp.carton_count = null;
      if (fieldValues.carton_count === "" || fieldValues.carton_count === 0) {
        temp.carton_count = "This field is required.";
      }
    }

    if ("unit_cost" in fieldValues) {
      temp.unit_cost = null;
      if (fieldValues.unit_cost === "" || fieldValues.unit_cost === 0) {
        temp.unit_cost = "This field is required.";
      }
    }

    if ("warehouse_id" in fieldValues) {
      temp.warehouse_id = null;
      if (fieldValues.warehouse_id) {
        if (fieldValues.warehouse_id.value === -1) {
          temp.warehouse_id = "This field is required.";
        }
      } else {
        temp.warehouse_id = "This field is required.";
      }
    }

    if ("warehouse_location_id" in fieldValues) {
      temp.warehouse_location_id = null;
      if (fieldValues.warehouse_location_id) {
        if (fieldValues.warehouse_location_id.value === -1) {
          temp.warehouse_location_id = "This field is required.";
        }
      } else {
        temp.warehouse_location_id = "This field is required.";
      }
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === null);
  };

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange,
    handleMultipleInputChange,
  } = useForm(initialFValues, true, validate);

  function resolveWarehouseId() {
    return product?.warehouse_id;
  }

  function resolveWarehouseLocationId() {
    return product?.warehouse_location_id;
  }

  function getSelectedWarehouse(warehouse_id, warehouses = warehouseOptions) {
    const id = warehouse_id ?? resolveWarehouseId();
    if (!id) return { value: null };

    const warehouseSelected = warehouses.find((el) => el.value === id);
    return warehouseSelected ?? { value: null };
  }

  function getSelectedWarehouseLocation(
    warehouse_location_id,
    wLocations = warehouseLocationsOptions
  ) {
    const id = warehouse_location_id ?? resolveWarehouseLocationId();
    if (!id) return { value: null };

    const warehouseLocationSelected = wLocations.find((el) => el.value === id);
    return warehouseLocationSelected ?? { value: null };
  }

  function resolveSelectedWeightType() {
    if (product?.stock_unit_of_measure === 2) {
      return { value: 3, label: "pieces" };
    }

    if (product?.stock_unit_of_measure) {
      return standardWeightOptions.find(
        (el) => el.value === product.stock_unit_of_measure
      );
    }

    return { value: -1, label: "" };
  }

  const onLoadData = async function () {
    if (!product) {
      setLoadingData(false);
      return;
    }

    setLoadingData(true);

    try {
      const warehouseId = resolveWarehouseId();
      const warehousesResult = await getWarehouses();
      if (warehousesResult.success) {
        setWarehouseOptions(warehousesResult.data);
      }

      const warehouseLocationsReslt = await getWarehouseLocations(warehouseId);
      if (warehouseLocationsReslt.success) {
        setWarehouseLocationsOptions(warehouseLocationsReslt.data);
      }

      const warehouseSelected = getSelectedWarehouse(
        warehouseId,
        warehousesResult.data
      );
      const warehouseLocationSelected = getSelectedWarehouseLocation(
        resolveWarehouseLocationId(),
        warehouseLocationsReslt.data
      );

      const selectedWeightType = resolveSelectedWeightType();
      const plu = product?.plu;

      if (isEdit) {
        setValues({
          ...values,
          ...product,
          weight_type: selectedWeightType,
          warehouse_id: warehouseSelected,
          warehouse_location_id: warehouseLocationSelected,
        });
      } else {
        const lotNumber = generateReceivingLotNumber(supplierNumber, plu);

        setValues({
          ...values,
          weight_type: selectedWeightType,
          product_id: product.id,
          product_name: product.name,
          plu,
          unit_cost: product.unit_cost,
          net_weight: 0,
          warehouse_id: warehouseSelected,
          warehouse_location_id: warehouseLocationSelected,
          lot_number: lotNumber,
          is_pallet: false,
        });
      }
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    onLoadData();
    // intentionally initialize when product changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const [inProgress, setInProgress] = useState(false);

  function calculateTotalCost(amount, unit_cost, carton_count) {
    const amountNum = Number(amount) || 0;
    const unitCostNum = Number(unit_cost) || 0;
    const cartonCountNum = Number(carton_count) || 0;
    return amountNum * unitCostNum * cartonCountNum;
  }

  function calculateUnitCost(amount, total_cost, carton_count) {
    const amountNum = Number(amount) || 0;
    const totalCostNum = Number(total_cost) || 0;
    const cartonCountNum = Number(carton_count) || 0;
    const divisor = amountNum * cartonCountNum;
    if (divisor === 0) {
      return 0;
    }
    return totalCostNum / divisor;
  }

  const handleGenerateLotNumber = () => {
    setValues({
      ...values,
      lot_number: generateReceivingLotNumber(supplierNumber, values.plu),
    });
  };

  const handleCopyLast = () => {
    if (!lastAddItemValues) return;

    setValues({
      ...values,
      net_weight: lastAddItemValues.net_weight,
      tare_weight: lastAddItemValues.tare_weight,
      carton_count: lastAddItemValues.carton_count,
      piece_count: lastAddItemValues.piece_count,
      weight_type: lastAddItemValues.weight_type,
      is_pallet: lastAddItemValues.is_pallet,
      total_cost: calculateTotalCost(
        lastAddItemValues.weight_type?.value === 3
          ? lastAddItemValues.piece_count
          : lastAddItemValues.net_weight,
        values.unit_cost,
        lastAddItemValues.carton_count
      ),
    });
  };

  const handleSubmit = async function () {
    setInProgress(true);
    if (validate()) {
      const result = await addItemToReceiving(values);
      if (result?.success) {
        lastAddItemValues = {
          net_weight: values.net_weight,
          tare_weight: values.tare_weight,
          carton_count: values.carton_count,
          piece_count: values.piece_count,
          weight_type: values.weight_type,
          is_pallet: values.is_pallet,
        };
      }
    }
    setInProgress(false);
  };

  const onLoadWarehouseLocations = async function (selectedWarehouse) {
    setValues({
      ...values,
      warehouse_id: selectedWarehouse,
      warehouse_location_id: null,
    });
    const result = await getWarehouseLocations(selectedWarehouse.value);
    if (result.success) {
      setWarehouseLocationsOptions(result.data);
    }
  };

  const weightTypeOptions =
    product?.stock_unit_of_measure === 2
      ? stockUnitOfMeasureOptions
      : standardWeightOptions;

  if (loadingData) {
    return (
      <div className="flex justify-center items-center py-16">
        <ProgressSpinner style={{ width: "40px", height: "40px" }} />
      </div>
    );
  }

  return (
    <form className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-md font-bold">{values.product_name}</p>
          <p className="text-md font-bold">{values.plu}</p>
        </div>
        {lastAddItemValues ? (
          <button
            type="button"
            className="text-sm text-indigo-600 hover:underline"
            onClick={handleCopyLast}
          >
            Copy last
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Input
            label="Lot Number"
            name="lot_number"
            onChange={handleInputChange}
            value={values.lot_number}
            labelOn={true}
            type="text"
            disabled={isEdit}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.lot_number}
          />
          {!isEdit ? (
            <button
              type="button"
              className="text-sm text-indigo-600 hover:underline mt-1"
              onClick={handleGenerateLotNumber}
            >
              Generate
            </button>
          ) : null}
        </div>

        <div className="grid grid-cols-3 col-span-2 gap-4">
          <Input
            label={`Net Weight ${values.weight_type.value === 3 ? "(kg)" : ""} per carton`}
            name="net_weight"
            onChange={(e) => {
              handleMultipleInputChange({
                net_weight: e.target.value,
                total_cost:
                  values.weight_type.value === 3
                    ? values.total_cost
                    : calculateTotalCost(
                        e.target.value,
                        values.unit_cost,
                        values.carton_count
                      ),
              });
            }}
            value={values.net_weight}
            labelOn={true}
            type="number"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.net_weight}
          />

          <Input
            label="Tare Weight"
            name="tare_weight"
            onChange={handleInputChange}
            value={values.tare_weight}
            labelOn={true}
            type="number"
            disabled={isEdit}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.tare_weight}
          />

          <SelectMenu
            handleInputChange={(e) => {
              const chosenUnit = e.target.value.value;
              const newTotalCost = calculateTotalCost(
                chosenUnit === 3 ? values.piece_count : values.net_weight,
                values.unit_cost,
                values.carton_count
              );
              var newPieceCount = values.piece_count;
              if (chosenUnit !== 3) {
                newPieceCount = 0;
              }

              setValues({
                ...values,
                weight_type: e.target.value,
                piece_count: newPieceCount,
                total_cost: newTotalCost,
              });
            }}
            name="weight_type"
            disabled={product.stock_unit_of_measure === 2}
            options={weightTypeOptions}
            value={values.weight_type}
            title={"Weight Unit"}
            error={errors.weight_type}
            showLabel={true}
          />
        </div>
      </div>
      <div
        className={classNames(
          values.weight_type.value === 3
            ? "grid grid-cols-4 gap-4"
            : "grid grid-cols-3 gap-4"
        )}
      >
        <Input
          label="Carton Count"
          name="carton_count"
          onChange={(e) => {
            handleMultipleInputChange({
              carton_count: e.target.value,
              total_cost: calculateTotalCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                values.unit_cost,
                e.target.value
              ),
            });
          }}
          value={values.carton_count}
          labelOn={true}
          type="number"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.carton_count}
        />

        <Input
          label="Piece Count per carton"
          name="piece_count"
          onChange={(e) => {
            handleMultipleInputChange({
              piece_count: e.target.value,
              total_cost: calculateTotalCost(
                e.target.value,
                values.unit_cost,
                values.carton_count
              ),
            });
          }}
          value={values.piece_count}
          error={errors.piece_count}
          labelOn={true}
          type="number"
          parentClass={classNames(
            values.weight_type.value === 3 ? null : "hidden"
          )}
        />

        <DollarInputText
          label="Unit Cost"
          name="unit_cost"
          onChange={(e) => {
            handleMultipleInputChange({
              unit_cost: e.target.value,
              total_cost: calculateTotalCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                e.target.value,
                values.carton_count
              ),
            });
          }}
          value={values.unit_cost}
          labelOn={true}
          type="number"
          className="mt-1 block w-full focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.unit_cost}
        />

        <DollarInputText
          label="Total Cost"
          name="total_cost"
          onChange={(e) => {
            const totalCost = e.value ?? e.target?.value;
            handleMultipleInputChange({
              total_cost: totalCost,
              unit_cost: calculateUnitCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                totalCost,
                values.carton_count
              ),
            });
          }}
          value={values.total_cost}
          labelOn={true}
          type="number"
          className="mt-1 block w-full focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.total_cost}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <SelectMenu
          handleInputChange={(e) => onLoadWarehouseLocations(e.target.value)}
          name="warehouse_id"
          options={warehouseOptions}
          value={values.warehouse_id}
          title={"Warehouse"}
          error={errors.warehouse_id}
          showLabel={true}
          menuPortalTarget={document.body}
        />

        <SelectMenu
          handleInputChange={handleInputChange}
          name="warehouse_location_id"
          options={warehouseLocationsOptions}
          value={values.warehouse_location_id}
          title={"Location"}
          error={errors.warehouse_location_id}
          showLabel={true}
          menuPortalTarget={document.body}
        />

        <label className="flex items-center gap-2 mt-6">
          <input
            type="checkbox"
            name="is_pallet"
            checked={values.is_pallet}
            onChange={(e) =>
              setValues({ ...values, is_pallet: e.target.checked })
            }
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
          <span className="text-sm font-medium text-gray-700">
            Create Pallet
          </span>
        </label>

      </div>
      <button
        type="button"
        className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        onClick={handleSubmit}
      >
        <ButtonSpinner spin={inProgress} text={isEdit ? "Save" : "Add"} />
      </button>
    </form>
  );
}
